import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CargoPaymentInfo } from 'src/app/models/cargo-payment-info.model';
import { Device } from 'src/app/models/device.model';
import { ContractType } from 'src/app/models/enums/contract-type.enum';
import { CustomMessage, mapMessage } from 'src/app/models/enums/custom-message.enum';
import { PaymentType } from 'src/app/models/enums/payment-type.enum';
import { OperationMode } from 'src/app/models/enums/operation-mode.enum';
import { SharedService } from 'src/app/services/shared-service/shared.service';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaymentComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() cargoPaymentInfo?: CargoPaymentInfo;
  @Output() modalTypeEvent = new EventEmitter<string>();
  currentContinueClick = false;
  currentNextClick = false;
  currentMobyDeviceError = false;
  currentSuccessfulPayment = false;
  currentPaymentMessage = '';
  currentErrorMessage = '';
  currentErrorCreditCard = false;
  currentCashPayment = false;
  currentCanceledByUser = false;
  currentSubmitBluefin = false;
  currentAuthCode: string | null = null;
  currentPaymentType = '';
  currentBluefinError = false;
  currentSelectedIpad: Device | null = null;
  isNoSignatureNoPaymentScenario = false;
  isCustomTimeoutError = false;
  paymentSectionExpanded = true;
  paymentSectionHidden = false;

  constructor(private sharedService: SharedService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sharedService.currentIsNoSignatureNoPayment.pipe(takeUntil(this.destroy$)).subscribe((isNoPayNoSignatureScenario) => {
      this.isNoSignatureNoPaymentScenario = isNoPayNoSignatureScenario;
      this.cdr.markForCheck();
    });

    this.sharedService.currentSelectedIpad.pipe(takeUntil(this.destroy$)).subscribe((selectedIpad) => {
      this.currentSelectedIpad = selectedIpad;
      this.cdr.markForCheck();
    });

    this.sharedService.currentBluefinError.pipe(takeUntil(this.destroy$)).subscribe((bluefinError) => {
      this.currentBluefinError = bluefinError;
      this.cdr.markForCheck();
    });

    this.sharedService.currentContinueClick.pipe(takeUntil(this.destroy$)).subscribe((isContinueClick) => {
      this.currentContinueClick = isContinueClick;
      this.cdr.markForCheck();
    });

    this.sharedService.currentNextClick.pipe(takeUntil(this.destroy$)).subscribe((isNextClick) => {
      this.currentNextClick = isNextClick;
      this.cdr.markForCheck();
    });

    this.sharedService.currentMobyDeviceError.pipe(takeUntil(this.destroy$)).subscribe((mobyDeviceError) => {
      this.currentMobyDeviceError = mobyDeviceError;
      this.cdr.markForCheck();
    });

    this.sharedService.currentPaymentMessage.pipe(takeUntil(this.destroy$)).subscribe((paymentMessage) => {
      this.currentPaymentMessage = paymentMessage;
      this.cdr.markForCheck();
    });

    this.sharedService.currentPaymentErrorMessage.pipe(takeUntil(this.destroy$)).subscribe((errorMessage) => {
      this.currentErrorMessage = errorMessage;
      this.cdr.markForCheck();
    });

    this.sharedService.currentErrorCreditCard.pipe(takeUntil(this.destroy$)).subscribe((errorCreditCard) => {
      this.currentErrorCreditCard = errorCreditCard;
      this.cdr.markForCheck();
    });

    this.sharedService.currentCashPayment.pipe(takeUntil(this.destroy$)).subscribe((cashPayment) => {
      this.currentCashPayment = cashPayment;
      this.cdr.markForCheck();
    });

    this.sharedService.currentCanceledByUser.pipe(takeUntil(this.destroy$)).subscribe((canceledByUser) => {
      this.currentCanceledByUser = canceledByUser;
      this.cdr.markForCheck();
    });

    this.sharedService.currentAuthCode.pipe(takeUntil(this.destroy$)).subscribe((authCode) => {
      this.currentAuthCode = authCode;
      this.cdr.markForCheck();
    });

    this.sharedService.currentSubmitBluefinClicked.pipe(takeUntil(this.destroy$)).subscribe((submitBluefin) => {
      this.currentSubmitBluefin = submitBluefin;
      this.cdr.markForCheck();
    });

    this.sharedService.currentIsCustomTimeoutError.pipe(takeUntil(this.destroy$)).subscribe((isCustomTimeoutError) => {
      this.isCustomTimeoutError = isCustomTimeoutError;
      this.cdr.markForCheck();
    });

    this.sharedService.currentSuccessfulPayment.pipe(takeUntil(this.destroy$)).subscribe((isCurrentSuccessfulPayment) => {
      this.currentSuccessfulPayment = isCurrentSuccessfulPayment;
      this.cdr.markForCheck();
    });

    this.sharedService.currentPaymentType.pipe(takeUntil(this.destroy$)).subscribe((paymentType) => {
      this.currentPaymentType = paymentType;
      this.cdr.markForCheck();
    });

    this.sharedService.currentPaymentSectionExpanded.pipe(takeUntil(this.destroy$)).subscribe((isExpanded) => {
      this.paymentSectionExpanded = isExpanded;
      this.cdr.markForCheck();
    });

    this.sharedService.currentPaymentSectionHidden.pipe(takeUntil(this.destroy$)).subscribe((isHidden) => {
      this.paymentSectionHidden = isHidden;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPaymentSelected(event: any) {
    if (this.cargoPaymentInfo) {
      this.sharedService.updateBluefinError(false);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const paymentType = Object.values(PaymentType)[Object.keys(PaymentType).indexOf(String(event.target.value))];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
      this.sharedService.updatePaymentType(paymentType);
      if (this.currentPaymentType === 'Manual Payment') {
        this.cargoPaymentInfo.signatureRequirement = 'NotRequired';
        this.sharedService.updateNextDisabled(false);
        this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnSignature));
      } else {
        //This is this.currentPaymentType = 'Customer Payment'
        this.cargoPaymentInfo.signatureRequirement = undefined;
        this.sharedService.hideMessage();
      }
    }
  }

  isValidated(): boolean {
    return this.cargoPaymentInfo?.signatureRequired?.toLocaleLowerCase() === 'y' &&
      this.cargoPaymentInfo?.signatureRequirement != undefined &&
      this.cargoPaymentInfo?.signatureRequirement != ContractType[0] &&
      (this.currentContinueClick || this.currentSubmitBluefin) &&
      this.cargoPaymentInfo?.paymentAdviceAmount > 0 &&
      this.cargoPaymentInfo?.operationMode != undefined &&
      (this.cargoPaymentInfo?.operationMode == OperationMode.CC || this.cargoPaymentInfo?.operationMode == OperationMode.CL)
      ? true
      : false;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  onCashChange($event: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const checked = $event.target.checked;
    if (checked) {
      this.sharedService.updateNextDisabled(false);
      this.sharedService.updateExitICargoDisabled(true);
      this.sharedService.updateDoneClickDisabled(false);
      this.sharedService.updateCashCheckbox(true);
      this.sharedService.updateIpadClick(false);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.SuccessPayment));
    } else {
      this.sharedService.updateNextDisabled(true);
      this.sharedService.updateExitICargoDisabled(false);
      this.sharedService.updateDoneClickDisabled(false);
      this.sharedService.updateCashCheckbox(false);
      this.sharedService.showMessage(mapMessage.get(CustomMessage.WarnCash));
    }
  }

  onEmitModalType(modalType: string) {
    this.modalTypeEvent.emit(modalType);
  }

  onShowPaymentInfo() {
    this.onEmitModalType('paymentInfo');
  }
}
